import React, { useEffect, useMemo, useState } from 'react';
import './DemoStand.css';

const pretty = (obj) => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
};

const safeJsonParse = (raw) => {
  if (!raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const normalizeWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

export const deriveWebSearchQuality = (meta, hits) => {
  const items = Array.isArray(hits) ? hits : [];
  const responseMeta = meta && typeof meta === 'object' ? meta : {};
  const unresponsive = Array.isArray(responseMeta?.meta?.unresponsive_engines)
    ? responseMeta.meta.unresponsive_engines
    : [];
  const blockedEngines = unresponsive
    .map((row) => Array.isArray(row) ? row.filter(Boolean).join(': ') : '')
    .filter(Boolean);
  const hasCaptcha = blockedEngines.some((row) => /captcha/i.test(row));
  const trustClasses = items
    .map((item) => String(item?.trust?.trust_class || '').trim().toLowerCase())
    .filter(Boolean);
  const strongHits = trustClasses.filter((cls) => ['official', 'institutional', 'major_media'].includes(cls)).length;
  const weakHits = trustClasses.filter((cls) => ['reference', 'unknown', 'low_trust'].includes(cls)).length;

  if (!responseMeta.ok && !items.length) {
    return {
      level: 'bad',
      title: 'Web search сейчас не ок',
      summary: String(responseMeta.error || 'Поиск не отдал результатов и не выглядит здоровым.'),
      details: blockedEngines,
    };
  }

  if (!items.length) {
    return {
      level: 'bad',
      title: 'Выдача пустая',
      summary: 'Провайдер ответил, но полезных внешних результатов нет.',
      details: blockedEngines,
    };
  }

  if (hasCaptcha || items.length === 1 || (strongHits === 0 && weakHits > 0)) {
    return {
      level: 'warn',
      title: 'Поиск жив, но качество слабое',
      summary: hasCaptcha
        ? 'Один из движков упёрся в CAPTCHA, поэтому выдача может быть кастрированной.'
        : 'Результаты есть, но они пока слабые по силе источников или их слишком мало.',
      details: blockedEngines,
    };
  }

  return {
    level: 'ok',
    title: 'Поиск выглядит рабочим',
    summary: 'Есть несколько результатов без явного деградационного сигнала по движкам.',
    details: blockedEngines,
  };
};

export const WEB_SEARCH_DEMO_PRESET = {
  label: 'OpenAI Search Demo',
  query: 'OpenAI official ChatGPT overview',
  extractUrl: 'https://openai.com/chatgpt/overview/',
};

export const pickPreferredExtractUrl = (hits, fallbackUrl = '') => {
  const items = Array.isArray(hits) ? hits : [];
  const ranked = [...items].sort((a, b) => {
    const aScore = Number(a?.trust?.trust_score || 0);
    const bScore = Number(b?.trust?.trust_score || 0);
    return bScore - aScore;
  });
  const preferred = ranked.find((item) => ['official', 'institutional'].includes(String(item?.trust?.trust_class || '').trim().toLowerCase()));
  const rawUrl = String(preferred?.url || ranked[0]?.url || fallbackUrl || '').trim();
  return rawUrl;
};

const buildExtractPreview = (result) => {
  const markdown = normalizeWhitespace(result?.markdown || '');
  const links = Array.isArray(result?.links) ? result.links : [];
  return {
    title: String(result?.title || '').trim() || 'Без заголовка',
    preview: markdown ? markdown.slice(0, 420) : '',
    chars: markdown.length,
    links: links.length,
    finalUrl: String(result?.url || '').trim(),
  };
};

const renderProviderHealth = (providers) => {
  const source = providers && typeof providers === 'object' ? providers : {};
  const order = ['search', 'extract', 'browser'];
  return (
    <div className="tool-provider-grid">
      {order.map((key) => {
        const item = source[key] || {};
        const health = item.health || {};
        const ok = !!health.ok;
        const label = key === 'search' ? 'Search' : key === 'extract' ? 'Extract' : 'Browser';
        return (
          <div key={key} className={`tool-provider-card ${ok ? 'is-ok' : 'is-bad'}`}>
            <div className="tool-provider-card__head">
              <strong>{label}</strong>
              <span className={`pill ${ok ? 'pill--ok' : 'pill--warn'}`}>{ok ? 'ok' : 'down'}</span>
            </div>
            <div className="muted">provider: {item.provider || '—'}</div>
            {key === 'search' && (
              <div className="muted">
                hits: {typeof health.hits === 'number' ? health.hits : '—'}
              </div>
            )}
            {key === 'extract' && (
              <div className="muted">title: {health.title || '—'}</div>
            )}
            {key === 'browser' && (
              <div className="muted">url: {health.final_url || '—'}</div>
            )}
            {!!health.error && <div className="error">{health.error}</div>}
          </div>
        );
      })}
    </div>
  );
};

const DemoStand = ({ apiBase, onExit }) => {
  const [health, setHealth] = useState({ ok: false, detail: 'unknown' });
  const [models, setModels] = useState([]);
  const [metrics, setMetrics] = useState(null);

  const [eventSource, setEventSource] = useState('user');
  const [eventType, setEventType] = useState('');
  const [eventContent, setEventContent] = useState('');
  const [eventMeta, setEventMeta] = useState('');
  const [eventEmbed, setEventEmbed] = useState(true);
  const [eventResult, setEventResult] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchK, setSearchK] = useState(8);
  const [searchResults, setSearchResults] = useState([]);
  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [webSearchK, setWebSearchK] = useState(5);
  const [webSearchResults, setWebSearchResults] = useState([]);
  const [webSearchMeta, setWebSearchMeta] = useState(null);
  const [webStatus, setWebStatus] = useState(null);
  const [extractUrl, setExtractUrl] = useState('');
  const [extractResult, setExtractResult] = useState(null);
  const [webDemoResult, setWebDemoResult] = useState(null);

  const [contextQuery, setContextQuery] = useState('');
  const [contextK, setContextK] = useState(10);
  const [contextRecent, setContextRecent] = useState(5);
  const [contextResult, setContextResult] = useState(null);

  const [rolesResult, setRolesResult] = useState(null);
  const [arbiterResult, setArbiterResult] = useState(null);
  const [blockBResult, setBlockBResult] = useState(null);

  const [actionType, setActionType] = useState('note');
  const [actionPayload, setActionPayload] = useState('{"text":"demo note"}');
  const [actionRunLimit, setActionRunLimit] = useState(5);
  const [actionsResult, setActionsResult] = useState(null);

  const [summaryLatest, setSummaryLatest] = useState(null);
  const [summaryRunResult, setSummaryRunResult] = useState(null);

  const [selfState, setSelfState] = useState(null);
  const [selfPatch, setSelfPatch] = useState(
    '{"mood":{"valence":0.1,"arousal":0.2},"values":["полезность"],"constraints":["не спамить память"]}'
  );
  const [selfResult, setSelfResult] = useState(null);
  const [selfPatchError, setSelfPatchError] = useState(null);

  const [linksLatest, setLinksLatest] = useState(null);
  const [linksType, setLinksType] = useState('causal');
  const [linksSource, setLinksSource] = useState('all');
  const [linksMinWeight, setLinksMinWeight] = useState('0.0');

  const [graphLimit, setGraphLimit] = useState(200);
  const [graphType, setGraphType] = useState('causal');
  const [graphSource, setGraphSource] = useState('all');
  const [graphMinWeight, setGraphMinWeight] = useState('0.0');
  const [graphSinceHours, setGraphSinceHours] = useState(24);
  const [graphResult, setGraphResult] = useState(null);

  const [metagraphRange, setMetagraphRange] = useState('weekly');
  const [metagraphLimit, setMetagraphLimit] = useState(500);
  const [metagraphLatest, setMetagraphLatest] = useState(null);
  const [metagraphRunResult, setMetagraphRunResult] = useState(null);
  const [metagraphLabelResult, setMetagraphLabelResult] = useState(null);

  const [loading, setLoading] = useState(false);
  const statusLabel = useMemo(() => (health.ok ? 'ONLINE' : 'OFFLINE'), [health.ok]);
  const webSearchQuality = useMemo(
    () => deriveWebSearchQuality(webSearchMeta, webSearchResults),
    [webSearchMeta, webSearchResults]
  );
  const extractPreview = useMemo(
    () => (extractResult?.ok ? buildExtractPreview(extractResult) : null),
    [extractResult]
  );

  const getHealth = async () => {
    try {
      const res = await fetch(`${apiBase}/health`);
      const data = await res.json();
      setHealth({ ok: !!data.ok, detail: data.detail || 'ok' });
    } catch (e) {
      setHealth({ ok: false, detail: e.message });
    }
  };

  const getModels = async () => {
    try {
      const res = await fetch(`${apiBase}/models`);
      const data = await res.json();
      setModels(Array.isArray(data.models) ? data.models : []);
    } catch {
      setModels([]);
    }
  };

  const getMetrics = async () => {
    try {
      const res = await fetch(`${apiBase}/metrics`);
      const data = await res.json();
      setMetrics(data);
    } catch {
      setMetrics(null);
    }
  };

  const getWebStatus = async () => {
    try {
      const res = await fetch(`${apiBase}/web/status`);
      const data = await res.json();
      setWebStatus(data);
    } catch (e) {
      setWebStatus({ ok: false, error: e.message });
    }
  };

  const getSummaryLatest = async () => {
    try {
      const res = await fetch(`${apiBase}/summaries/latest`);
      const data = await res.json();
      setSummaryLatest(data.summary || null);
    } catch {
      setSummaryLatest(null);
    }
  };

  const runSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/summaries/run`, { method: 'POST' });
      const data = await res.json();
      setSummaryRunResult(data);
      if (data?.result?.ok) {
        await getSummaryLatest();
      }
    } catch (e) {
      setSummaryRunResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const getLinksLatest = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/links/latest?limit=20&type=${encodeURIComponent(linksType)}&source=${encodeURIComponent(linksSource)}&min_weight=${encodeURIComponent(linksMinWeight)}`
      );
      const data = await res.json();
      setLinksLatest(data);
    } catch (e) {
      setLinksLatest({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const getGraphLinks = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        limit: String(graphLimit || 200),
        link_type: graphType,
        source: graphSource,
        min_weight: String(graphMinWeight || 0),
        since_hours: String(graphSinceHours || 0)
      });
      const res = await fetch(`${apiBase}/graph/links?${qs.toString()}`);
      const data = await res.json();
      setGraphResult(data);
    } catch (e) {
      setGraphResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const getMetagraphLatest = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/graph/metagraph/latest?range_label=${encodeURIComponent(metagraphRange)}`);
      const data = await res.json();
      setMetagraphLatest(data);
    } catch (e) {
      setMetagraphLatest({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const runMetagraph = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/graph/metagraph/run?range_label=${encodeURIComponent(metagraphRange)}&limit=${encodeURIComponent(metagraphLimit)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setMetagraphRunResult(data);
      await getMetagraphLatest();
    } catch (e) {
      setMetagraphRunResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const labelMetagraph = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/graph/metagraph/label?range_label=${encodeURIComponent(metagraphRange)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      setMetagraphLabelResult(data);
      await getMetagraphLatest();
    } catch (e) {
      setMetagraphLabelResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const renderLinksList = () => {
    if (!linksLatest || !linksLatest.items || !Array.isArray(linksLatest.items)) {
      return <div className="muted">Нет данных</div>;
    }
    if (linksLatest.items.length === 0) {
      return <div className="muted">Нет связей</div>;
    }
    return (
      <div className="links-list">
        {linksLatest.items.map((item) => (
          <div key={`link-${item.id}`} className="links-row">
            <span className="pill pill--cool">{item.type}</span>
            <div className="links-node">
              <div className="node-meta">{item.a.source} · {item.a.id}</div>
              <div className="node-text">{item.a.content}</div>
            </div>
            <div className="links-arrow">→</div>
            <div className="links-node">
              <div className="node-meta">{item.b.source} · {item.b.id}</div>
              <div className="node-text">{item.b.content}</div>
            </div>
            <div className="node-weight">{item.weight.toFixed(2)}</div>
          </div>
        ))}
      </div>
    );
  };

  const getSelf = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/self`);
      const data = await res.json();
      setSelfState(data);
    } catch (e) {
      setSelfState({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const patchSelf = async () => {
    const payload = safeJsonParse(selfPatch);
    if (!payload) {
      setSelfResult({ ok: false, error: 'invalid_json' });
      setSelfPatchError('Invalid JSON');
      return;
    }
    setSelfPatchError(null);
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/self`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      setSelfResult(data);
      if (data?.ok) {
        setSelfState(data);
      }
    } catch (e) {
      setSelfResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const formatSelfPatch = () => {
    const parsed = safeJsonParse(selfPatch);
    if (!parsed) {
      setSelfPatchError('Invalid JSON');
      return;
    }
    setSelfPatch(JSON.stringify(parsed, null, 2));
    setSelfPatchError(null);
  };

  const renderSelfSummary = () => {
    const current = selfState?.data && selfState.ok ? selfState.data : null;
    if (!current) return <div className="muted">Нет данных self_state</div>;
    const goals = Array.isArray(current.goals) ? current.goals : [];
    const values = Array.isArray(current.values) ? current.values : [];
    const constraints = Array.isArray(current.constraints) ? current.constraints : [];
    const mood = current.mood || {};
    const valence = typeof mood.valence === 'number' ? mood.valence : 0;
    const arousal = typeof mood.arousal === 'number' ? mood.arousal : 0;

    return (
      <div className="self-summary">
        <div className="self-section">
          <div className="label">Goals</div>
          <div className="pill-list">
            {goals.length === 0 && <span className="muted">—</span>}
            {goals.map((g, idx) => (
              <span key={`goal-${idx}`} className="pill pill--accent">{g}</span>
            ))}
          </div>
        </div>
        <div className="self-section">
          <div className="label">Values</div>
          <div className="pill-list">
            {values.length === 0 && <span className="muted">—</span>}
            {values.map((v, idx) => (
              <span key={`val-${idx}`} className="pill pill--cool">{v}</span>
            ))}
          </div>
        </div>
        <div className="self-section">
          <div className="label">Constraints</div>
          <div className="pill-list">
            {constraints.length === 0 && <span className="muted">—</span>}
            {constraints.map((c, idx) => (
              <span key={`cons-${idx}`} className="pill pill--warn">{c}</span>
            ))}
          </div>
        </div>
        <div className="self-section">
          <div className="label">Mood</div>
          <div className="mood-grid">
            <div className="mood-row">
              <span className="mood-label">Valence</span>
              <div className="mood-bar">
                <div className="mood-fill" style={{ width: `${Math.min(Math.max((valence + 1) * 50, 0), 100)}%` }} />
              </div>
              <span className="mood-value">{valence.toFixed(2)}</span>
            </div>
            <div className="mood-row">
              <span className="mood-label">Arousal</span>
              <div className="mood-bar">
                <div className="mood-fill mood-fill--cool" style={{ width: `${Math.min(Math.max(arousal * 100, 0), 100)}%` }} />
              </div>
              <span className="mood-value">{arousal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    getHealth();
    getModels();
    getMetrics();
    getWebStatus();
    getSummaryLatest();
    getSelf();
    getLinksLatest();
    getMetagraphLatest();
  }, []);

  const postJson = async (path, body) => {
    const res = await fetch(`${apiBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    return res.json();
  };

  const handleIngest = async () => {
    if (!eventContent.trim()) return;
    setLoading(true);
    const meta = safeJsonParse(eventMeta);
    try {
      const data = await postJson('/events', {
        source: eventSource || null,
        type: eventType?.trim() || null,
        content: eventContent,
        meta: meta,
        embed: eventEmbed
      });
      setEventResult(data);
      setEventContent('');
    } catch (e) {
      setEventResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const data = await postJson('/memory/search', { query: searchQuery, k: Number(searchK) || 8 });
      setSearchResults(data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWebSearch = async () => {
    if (!webSearchQuery.trim()) return;
    setLoading(true);
    try {
      const data = await postJson('/web/search', { query: webSearchQuery, k: Number(webSearchK) || 5 });
      const hits = Array.isArray(data.hits) ? data.hits : [];
      const preferredUrl = pickPreferredExtractUrl(hits, '');
      setWebSearchResults(hits);
      setWebSearchMeta(data);
      setWebDemoResult(null);
      setExtractResult(null);
      setExtractUrl(preferredUrl);
    } catch (e) {
      setWebSearchResults([]);
      setWebSearchMeta({ ok: false, error: e.message });
      setWebDemoResult(null);
      setExtractResult(null);
      setExtractUrl('');
    } finally {
      setLoading(false);
    }
  };

  const handleExtract = async () => {
    if (!extractUrl.trim()) return;
    setLoading(true);
    try {
      const data = await postJson('/web/extract', { url: extractUrl, timeout_s: 20 });
      setExtractResult(data);
    } catch (e) {
      setExtractResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRunWebDemo = async () => {
    setLoading(true);
    try {
      const [statusRes, searchData] = await Promise.all([
        fetch(`${apiBase}/web/status`).then((res) => res.json()),
        postJson('/web/search', { query: WEB_SEARCH_DEMO_PRESET.query, k: 5 }),
      ]);
      const hits = Array.isArray(searchData?.hits) ? searchData.hits : [];
      const preferredUrl = pickPreferredExtractUrl(hits, WEB_SEARCH_DEMO_PRESET.extractUrl);
      setWebStatus(statusRes);
      setWebSearchQuery(WEB_SEARCH_DEMO_PRESET.query);
      setWebSearchResults(hits);
      setWebSearchMeta(searchData);
      setExtractUrl(preferredUrl);

      let extractData = null;
      if (preferredUrl) {
        extractData = await postJson('/web/extract', { url: preferredUrl, timeout_s: 20 });
        setExtractResult(extractData);
      } else {
        setExtractResult(null);
      }

      setWebDemoResult({
        ok: true,
        preset: WEB_SEARCH_DEMO_PRESET.label,
        query: WEB_SEARCH_DEMO_PRESET.query,
        chosen_url: preferredUrl,
        hits: hits.length,
        search_ok: !!searchData?.ok,
        extract_ok: !!extractData?.ok,
      });
    } catch (e) {
      setWebDemoResult({
        ok: false,
        preset: WEB_SEARCH_DEMO_PRESET.label,
        error: e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContext = async () => {
    if (!contextQuery.trim()) return;
    setLoading(true);
    try {
      const data = await postJson('/context', {
        query: contextQuery,
        k: Number(contextK) || 10,
        recent: Number(contextRecent) || 5
      });
      setContextResult(data);
    } catch (e) {
      setContextResult({ ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRoles = async () => {
    if (!contextResult?.context_text) return;
    setLoading(true);
    try {
      const data = await postJson('/roles', { context_text: contextResult.context_text });
      setRolesResult(data.roles || []);
    } catch (e) {
      setRolesResult([{ error: e.message }]);
    } finally {
      setLoading(false);
    }
  };

  const handleArbiter = async () => {
    if (!rolesResult || !Array.isArray(rolesResult)) return;
    setLoading(true);
    try {
      const data = await postJson('/arbiter', { role_outputs: rolesResult });
      setArbiterResult(data.result || data);
    } catch (e) {
      setArbiterResult({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleBlockB = async () => {
    if (!arbiterResult) return;
    setLoading(true);
    try {
      const data = await postJson('/block_b', { payload: arbiterResult });
      setBlockBResult(data);
    } catch (e) {
      setBlockBResult({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFullTick = async () => {
    if (!contextQuery.trim()) return;
    setLoading(true);
    try {
      const ctx = await postJson('/context', {
        query: contextQuery,
        k: Number(contextK) || 10,
        recent: Number(contextRecent) || 5
      });
      setContextResult(ctx);

      const roles = await postJson('/roles', { context_text: ctx.context_text || '' });
      setRolesResult(roles.roles || []);

      const arb = await postJson('/arbiter', { role_outputs: roles.roles || [] });
      setArbiterResult(arb.result || arb);

      const block = await postJson('/block_b', { payload: arb.result || arb });
      setBlockBResult(block);
    } catch (e) {
      setBlockBResult({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEnqueueAction = async () => {
    setLoading(true);
    try {
      const payload = safeJsonParse(actionPayload) || {};
      const data = await postJson('/actions/enqueue', { type: actionType, payload });
      setActionsResult(data);
    } catch (e) {
      setActionsResult({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRunActions = async () => {
    setLoading(true);
    try {
      const data = await postJson('/actions/run', { limit: Number(actionRunLimit) || 5 });
      setActionsResult(data);
    } catch (e) {
      setActionsResult({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="demo-stand">
      <header className="demo-top">
        <button className="btn" onClick={onExit}>← В чат</button>
      </header>

      <header className="hero">
        <div className="hero__title">
          <div className="badge">Fractal Core • Demo Stand</div>
          <h1>Наблюдаемая когнитивная система в реальном времени</h1>
          <p>
            Цепочка: Ingest → Context → Roles → Arbiter → Block B → Actions.
            Демонстрация работает напрямую через API.
          </p>
        </div>
        <div className="hero__meta">
          <div className={`status ${health.ok ? 'ok' : 'bad'}`}>
            <span className="dot" />
            <div>
              <strong>{statusLabel}</strong>
              <div className="muted">{health.detail}</div>
            </div>
          </div>
          <div className="panel">
            <div className="panel__row">
              <span>API</span>
              <strong>{apiBase}</strong>
            </div>
            <div className="panel__row">
              <span>Models</span>
              <strong>{models.length}</strong>
            </div>
          </div>
          <div className="buttons">
            <button className="btn" onClick={getHealth}>Check health</button>
            <button className="btn" onClick={getModels}>Reload models</button>
            <button className="btn" onClick={getMetrics}>Refresh metrics</button>
            <button className="btn" onClick={getWebStatus}>Tool stack</button>
          </div>
        </div>
      </header>

      <main className="grid">
        <section className="card">
          <h2>Ingest событий</h2>
          <label>Источник</label>
          <input value={eventSource} onChange={(e) => setEventSource(e.target.value)} />
          <label>Тип (онтология)</label>
          <input value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder="intent/feedback/state/task/observation" />
          <label>Содержимое</label>
          <textarea rows={4} value={eventContent} onChange={(e) => setEventContent(e.target.value)} />
          <label>Meta JSON (опционально)</label>
          <textarea rows={3} value={eventMeta} onChange={(e) => setEventMeta(e.target.value)} />
          <label className="row">
            <input type="checkbox" checked={eventEmbed} onChange={(e) => setEventEmbed(e.target.checked)} />
            <span>Создавать embedding</span>
          </label>
          <button className="btn primary" onClick={handleIngest} disabled={loading}>Добавить событие</button>
          <pre className="output">{eventResult ? pretty(eventResult) : '—'}</pre>
        </section>

        <section className="card">
          <h2>Поиск по памяти (pgvector)</h2>
          <label>Запрос</label>
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          <label>Top‑K</label>
          <input type="number" value={searchK} onChange={(e) => setSearchK(e.target.value)} />
          <button className="btn" onClick={handleSearch} disabled={loading}>Memory search</button>
          <div className="list">
            {searchResults.length === 0 && <div className="muted">Нет результатов</div>}
            {searchResults.map((item) => (
              <div key={item.id} className="list__item">
                <div className="pill">#{item.id}</div>
                <div className="pill muted">{item.source || 'unknown'}</div>
                <div className="pill muted">{item.type || '-'}</div>
                <div>{item.content}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <h2>Web Tooling</h2>
          {renderProviderHealth(webStatus?.providers)}
          <div className="demo-callout">
            <div>
              <strong>One-click demo</strong>
              <div className="muted">
                Прогоняет status, web search и extract на одном каноническом запросе.
              </div>
            </div>
            <button className="btn primary" onClick={handleRunWebDemo} disabled={loading}>Run search demo</button>
          </div>
          {webDemoResult && (
            <div className={`tool-quality tool-quality--${webDemoResult.ok ? 'ok' : 'bad'}`}>
              <strong>{webDemoResult.ok ? 'Demo completed' : 'Demo failed'}</strong>
              <div>
                {webDemoResult.ok
                  ? `${webDemoResult.hits} hits, chosen URL: ${webDemoResult.chosen_url || '—'}`
                  : String(webDemoResult.error || 'search demo failed')}
              </div>
            </div>
          )}
          <pre className="output">{webStatus ? pretty(webStatus) : '—'}</pre>
          <label>Внешний запрос</label>
          <input value={webSearchQuery} onChange={(e) => setWebSearchQuery(e.target.value)} placeholder="например: latest fractal tooling stack" />
          <label>Top‑K</label>
          <input type="number" value={webSearchK} onChange={(e) => setWebSearchK(e.target.value)} />
          <button className="btn" onClick={handleWebSearch} disabled={loading}>Web search</button>
          {webSearchQuality && (
            <div className={`tool-quality tool-quality--${webSearchQuality.level}`}>
              <strong>{webSearchQuality.title}</strong>
              <div>{webSearchQuality.summary}</div>
              {webSearchQuality.details.length > 0 && (
                <div className="muted">
                  degraded engines: {webSearchQuality.details.join(' · ')}
                </div>
              )}
            </div>
          )}
          <div className="list">
            {webSearchResults.length === 0 && <div className="muted">Нет внешних результатов</div>}
            {webSearchResults.map((item, idx) => (
              <div key={`${item.url}-${idx}`} className="list__item">
                <div className="pill">{item.source || 'searxng'}</div>
                <div className="pill muted">{item?.trust?.trust_class || 'unknown'}</div>
                <div><strong>{item.title || item.url}</strong></div>
                <div className="muted">{item.url}</div>
                <div className="muted">trust score: {typeof item?.trust?.trust_score === 'number' ? item.trust.trust_score.toFixed(2) : '—'}</div>
                {!!item?.trust?.trust_reason && <div className="muted">trust reason: {item.trust.trust_reason}</div>}
                {item.snippet && <div>{item.snippet}</div>}
              </div>
            ))}
          </div>
          <details className="details-box">
            <summary>Raw search payload</summary>
            <pre className="output">{webSearchMeta ? pretty(webSearchMeta) : '—'}</pre>
          </details>
          <label>Extract URL</label>
          <input value={extractUrl} onChange={(e) => setExtractUrl(e.target.value)} placeholder="https://example.org" />
          <button className="btn" onClick={handleExtract} disabled={loading}>Extract page</button>
          {extractPreview && (
            <div className="extract-preview">
              <div className="extract-preview__head">
                <strong>{extractPreview.title}</strong>
                <span className="pill pill--cool">{extractPreview.chars} chars</span>
                <span className="pill">{extractPreview.links} links</span>
              </div>
              <div className="muted">{extractPreview.finalUrl}</div>
              <div className="extract-preview__body">
                {extractPreview.preview || 'Пустой extract.'}
              </div>
            </div>
          )}
          <details className="details-box">
            <summary>Raw extract payload</summary>
            <pre className="output">{extractResult ? pretty(extractResult) : '—'}</pre>
          </details>
        </section>

        <section className="card wide">
          <h2>Context Builder (Block C)</h2>
          <div className="row split">
            <div>
              <label>Запрос</label>
              <input value={contextQuery} onChange={(e) => setContextQuery(e.target.value)} />
            </div>
            <div>
              <label>Top‑K</label>
              <input type="number" value={contextK} onChange={(e) => setContextK(e.target.value)} />
            </div>
            <div>
              <label>Recent</label>
              <input type="number" value={contextRecent} onChange={(e) => setContextRecent(e.target.value)} />
            </div>
          </div>
          <div className="buttons">
            <button className="btn" onClick={handleContext} disabled={loading}>Build context</button>
            <button className="btn primary" onClick={handleFullTick} disabled={loading}>Run full tick</button>
          </div>
          <pre className="output">{contextResult ? contextResult.context_text || pretty(contextResult) : '—'}</pre>
          <div className="list">
            {(contextResult?.items || []).length === 0 && <div className="muted">Контекст пуст</div>}
            {(contextResult?.items || []).map((item) => (
              <div key={item.id} className="list__item">
                <div className="pill">#{item.id}</div>
                <div className="pill muted">{item.source || 'unknown'}</div>
                <div className="pill muted">{item.type || '-'}</div>
                <div>{item.content}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="card">
          <h2>Role Pool</h2>
          <button className="btn" onClick={handleRoles} disabled={loading || !contextResult?.context_text}>Run roles</button>
          <pre className="output">{rolesResult ? pretty(rolesResult) : '—'}</pre>
        </section>

        <section className="card">
          <h2>Arbiter</h2>
          <button className="btn" onClick={handleArbiter} disabled={loading || !rolesResult}>Merge roles</button>
          <pre className="output">{arbiterResult ? pretty(arbiterResult) : '—'}</pre>
        </section>

        <section className="card">
          <h2>Block B (Memory write)</h2>
          <button className="btn" onClick={handleBlockB} disabled={loading || !arbiterResult}>Write memory</button>
          <pre className="output">{blockBResult ? pretty(blockBResult) : '—'}</pre>
        </section>

        <section className="card">
          <h2>Actions</h2>
          <label>Тип</label>
          <select value={actionType} onChange={(e) => setActionType(e.target.value)}>
            <option value="note">note</option>
            <option value="search">search</option>
            <option value="noop">noop</option>
          </select>
          <label>Payload JSON</label>
          <textarea rows={3} value={actionPayload} onChange={(e) => setActionPayload(e.target.value)} />
          <div className="buttons">
            <button className="btn" onClick={handleEnqueueAction} disabled={loading}>Enqueue</button>
            <button className="btn" onClick={handleRunActions} disabled={loading}>Run actions</button>
          </div>
          <label>Limit</label>
          <input type="number" value={actionRunLimit} onChange={(e) => setActionRunLimit(e.target.value)} />
          <pre className="output">{actionsResult ? pretty(actionsResult) : '—'}</pre>
        </section>

        <section className="card">
          <h2>Metrics</h2>
          <pre className="output">{metrics ? pretty(metrics) : '—'}</pre>
        </section>

        <section className="card">
          <h2>Summaries</h2>
          <div className="buttons">
            <button className="btn" onClick={getSummaryLatest} disabled={loading}>Refresh latest</button>
            <button className="btn primary" onClick={runSummary} disabled={loading}>Run digest</button>
          </div>
          <pre className="output">{summaryLatest ? pretty(summaryLatest) : '—'}</pre>
          <pre className="output">{summaryRunResult ? pretty(summaryRunResult) : '—'}</pre>
        </section>

        <section className="card wide">
          <h2>SelfState</h2>
          <div className="buttons">
            <button className="btn" onClick={getSelf} disabled={loading}>Refresh</button>
            <button className="btn primary" onClick={patchSelf} disabled={loading}>Apply patch</button>
            <button className="btn" onClick={formatSelfPatch} disabled={loading}>Format JSON</button>
          </div>
          <div className="split-grid">
            <div>
              {renderSelfSummary()}
              <label>Current</label>
              <pre className="output">{selfState ? pretty(selfState) : '—'}</pre>
            </div>
            <div>
              <label>Patch (JSON)</label>
              <textarea
                rows="10"
                className={selfPatchError ? 'input-error' : ''}
                value={selfPatch}
                onChange={(e) => {
                  setSelfPatch(e.target.value);
                  const ok = safeJsonParse(e.target.value);
                  setSelfPatchError(ok ? null : 'Invalid JSON');
                }}
              />
              {selfPatchError && <div className="error">{selfPatchError}</div>}
              <pre className="output">{selfResult ? pretty(selfResult) : '—'}</pre>
            </div>
          </div>
        </section>

        <section className="card wide">
          <h2>Links</h2>
          <div className="row split">
            <div>
              <label>Type</label>
              <select value={linksType} onChange={(e) => setLinksType(e.target.value)}>
                <option value="causal">causal</option>
                <option value="semantic">semantic</option>
                <option value="">all</option>
              </select>
            </div>
            <div>
              <label>Source</label>
              <select value={linksSource} onChange={(e) => setLinksSource(e.target.value)}>
                <option value="all">all</option>
                <option value="user">user</option>
                <option value="decision">decision</option>
                <option value="action">action</option>
                <option value="memory_write">memory_write</option>
                <option value="self_state">self_state</option>
              </select>
            </div>
            <div>
              <label>Min weight</label>
              <input
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={linksMinWeight}
                onChange={(e) => setLinksMinWeight(e.target.value)}
              />
            </div>
            <div className="buttons">
              <button className="btn" onClick={getLinksLatest} disabled={loading}>Refresh</button>
            </div>
          </div>
          {renderLinksList()}
          <pre className="output">{linksLatest ? pretty(linksLatest) : '—'}</pre>
        </section>

        <section className="card wide">
          <h2>Graph API (nodes + edges)</h2>
          <div className="row split">
            <div>
              <label>Type</label>
              <input value={graphType} onChange={(e) => setGraphType(e.target.value)} />
            </div>
            <div>
              <label>Source</label>
              <input value={graphSource} onChange={(e) => setGraphSource(e.target.value)} />
            </div>
            <div>
              <label>Min weight</label>
              <input value={graphMinWeight} onChange={(e) => setGraphMinWeight(e.target.value)} />
            </div>
            <div>
              <label>Since hours</label>
              <input type="number" value={graphSinceHours} onChange={(e) => setGraphSinceHours(e.target.value)} />
            </div>
            <div>
              <label>Limit</label>
              <input type="number" value={graphLimit} onChange={(e) => setGraphLimit(e.target.value)} />
            </div>
          </div>
          <div className="buttons">
            <button className="btn" onClick={getGraphLinks} disabled={loading}>Load graph</button>
          </div>
          <div className="list">
            <div className="muted">
              {graphResult?.meta ? `nodes=${graphResult.meta.nodes} edges=${graphResult.meta.edges}` : '—'}
            </div>
            {(graphResult?.edges || []).slice(0, 20).map((edge) => (
              <div key={`edge-${edge.id}`} className="list__item">
                <div className="pill">{edge.type}</div>
                <div className="pill muted">w={edge.weight.toFixed(2)}</div>
                <div className="pill muted">a:{edge.a}</div>
                <div className="pill muted">b:{edge.b}</div>
              </div>
            ))}
          </div>
          <pre className="output">{graphResult ? pretty(graphResult.meta || graphResult) : '—'}</pre>
        </section>

        <section className="card wide">
          <h2>Metagraph (clusters)</h2>
          <div className="row split">
            <div>
              <label>Range</label>
              <input value={metagraphRange} onChange={(e) => setMetagraphRange(e.target.value)} />
            </div>
            <div>
              <label>Limit</label>
              <input type="number" value={metagraphLimit} onChange={(e) => setMetagraphLimit(e.target.value)} />
            </div>
          </div>
          <div className="buttons">
            <button className="btn" onClick={getMetagraphLatest} disabled={loading}>Latest</button>
            <button className="btn" onClick={runMetagraph} disabled={loading}>Run</button>
            <button className="btn" onClick={labelMetagraph} disabled={loading}>Label</button>
          </div>
          <div className="list">
            {(metagraphLatest?.data?.clusters || []).length === 0 && <div className="muted">Нет кластеров</div>}
            {(metagraphLatest?.data?.clusters || []).map((c) => (
              <div key={`cluster-${c.cluster_id}`} className="list__item">
                <div className="pill">#{c.cluster_id}</div>
                <div className="pill muted">size={c.size}</div>
                <div className="pill pill--cool">{c.label || '—'}</div>
                <div>{(c.keywords || []).join(', ')}</div>
              </div>
            ))}
          </div>
          <pre className="output">{metagraphLatest ? pretty(metagraphLatest.data || metagraphLatest) : '—'}</pre>
          <pre className="output">{metagraphRunResult ? pretty(metagraphRunResult) : '—'}</pre>
          <pre className="output">{metagraphLabelResult ? pretty(metagraphLabelResult) : '—'}</pre>
        </section>

        <section className="card wide">
          <h2>Models</h2>
          <div className="list">
            {models.length === 0 && <div className="muted">Нет доступных моделей</div>}
            {models.map((name) => (
              <div key={name} className="list__item">
                <div className="pill">model</div>
                <div>{name}</div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="footer">
        <div>Fractal Core · Demo Stand</div>
        <div className="muted">Наблюдаемость > философия. Сначала работающий цикл.</div>
      </footer>
    </div>
  );
};

export default DemoStand;
